<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-6">
      <!-- Hero Section -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
        <p class="text-xl text-gray-700 mb-8 leading-relaxed">
          Get in touch with our team. We're here to help with any questions about our services.
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-lg shadow-sm p-8">
        <!-- Contact Information Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">Get in Touch</h2>
          
          <div class="grid md:grid-cols-3 gap-6">
            <!-- Email -->
            <div class="text-center p-6 bg-blue-50 rounded-xl">
              <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-envelope text-white text-xl"></i>
              </div>
              <h4 class="font-semibold text-gray-800 mb-2">Email Us</h4>
              <p class="text-gray-600 mb-2">support@guest5of5.com</p>
              <p class="text-sm text-gray-500">We'll respond within 24 hours</p>
            </div>
            
            <!-- Phone -->
            <div class="text-center p-6 bg-green-50 rounded-xl">
              <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-telephone text-white text-xl"></i>
              </div>
              <h4 class="font-semibold text-gray-800 mb-2">Call Us</h4>
              <p class="text-gray-600 mb-2">+1 (555) 123-4567</p>
              <p class="text-sm text-gray-500">Mon-Fri: 9AM-6PM EST</p>
            </div>
            
            <!-- Live Chat -->
            <div class="text-center p-6 bg-purple-50 rounded-xl">
              <div class="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-chat-dots text-white text-xl"></i>
              </div>
              <h4 class="font-semibold text-gray-800 mb-2">Live Chat</h4>
              <p class="text-gray-600 mb-2">Available 24/7</p>
              <p class="text-sm text-gray-500">Instant support</p>
            </div>
          </div>
        </section>

        <!-- Contact Form Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h2>
          
          <form @submit.prevent="submitForm" class="space-y-6">
            <div class="grid md:grid-cols-2 gap-6">
              <!-- First Name -->
              <div>
                <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <input 
                  v-model="form.firstName"
                  type="text" 
                  id="firstName"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your first name"
                  required
                />
              </div>
              
              <!-- Last Name -->
              <div>
                <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <input 
                  v-model="form.lastName"
                  type="text" 
                  id="lastName"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input 
                v-model="form.email"
                type="email" 
                id="email"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
                required
              />
            </div>

            <!-- Phone -->
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input 
                v-model="form.phone"
                type="tel" 
                id="phone"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your phone number"
              />
            </div>

            <!-- Subject -->
            <div>
              <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
              <select 
                v-model="form.subject"
                id="subject"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select a subject</option>
                <option value="general">General Inquiry</option>
                <option value="booking">Booking Support</option>
                <option value="technical">Technical Support</option>
                <option value="billing">Billing Question</option>
                <option value="partnership">Partnership</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <!-- Message -->
            <div>
              <label for="message" class="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea 
                v-model="form.message"
                id="message"
                rows="6"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Tell us how we can help you..."
                required
              ></textarea>
            </div>

            <!-- Submit Button -->
            <div class="text-center">
              <button 
                type="submit"
                :disabled="submitting"
                class="px-8 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submitting">
                  <i class="bi bi-arrow-clockwise animate-spin mr-2"></i>
                  Sending...
                </span>
                <span v-else>
                  <i class="bi bi-send mr-2"></i>
                  Send Message
                </span>
              </button>
            </div>
          </form>
        </section>

        <!-- Office Locations Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">Our Offices</h2>
          
          <div class="grid md:grid-cols-2 gap-8">
            <!-- Main Office -->
            <div class="p-6 bg-gray-50 rounded-xl">
              <div class="flex items-start space-x-4">
                <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i class="bi bi-geo-alt text-white text-xl"></i>
                </div>
                <div>
                  <h4 class="font-semibold text-gray-800 mb-2">Main Office</h4>
                  <p class="text-gray-600 mb-2">123 Business Street</p>
                  <p class="text-gray-600 mb-2">New York, NY 10001</p>
                  <p class="text-gray-600">United States</p>
                </div>
              </div>
            </div>
            
            <!-- Support Center -->
            <div class="p-6 bg-gray-50 rounded-xl">
              <div class="flex items-start space-x-4">
                <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i class="bi bi-headset text-white text-xl"></i>
                </div>
                <div>
                  <h4 class="font-semibold text-gray-800 mb-2">Support Center</h4>
                  <p class="text-gray-600 mb-2">456 Tech Avenue</p>
                  <p class="text-gray-600 mb-2">San Francisco, CA 94105</p>
                  <p class="text-gray-600">United States</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- FAQ Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div class="space-y-4">
            <div class="border border-gray-200 rounded-lg">
              <button 
                @click="toggleFAQ(1)"
                class="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition"
              >
                <span class="font-semibold text-gray-800">How do I book a hotel room?</span>
                <i class="bi" :class="openFAQ === 1 ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <div v-if="openFAQ === 1" class="px-6 pb-4">
                <p class="text-gray-600">You can book a hotel room by browsing our available hotels, selecting your dates, and completing the booking process through our secure platform.</p>
              </div>
            </div>
            
            <div class="border border-gray-200 rounded-lg">
              <button 
                @click="toggleFAQ(2)"
                class="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition"
              >
                <span class="font-semibold text-gray-800">What is the cancellation policy?</span>
                <i class="bi" :class="openFAQ === 2 ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <div v-if="openFAQ === 2" class="px-6 pb-4">
                <p class="text-gray-600">Cancellation policies vary by hotel. You can view the specific cancellation policy for each booking before confirming your reservation.</p>
              </div>
            </div>
            
            <div class="border border-gray-200 rounded-lg">
              <button 
                @click="toggleFAQ(3)"
                class="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition"
              >
                <span class="font-semibold text-gray-800">How do I contact customer support?</span>
                <i class="bi" :class="openFAQ === 3 ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <div v-if="openFAQ === 3" class="px-6 pb-4">
                <p class="text-gray-600">You can contact our customer support team via email, phone, or live chat. We're available 24/7 to assist you with any questions or concerns.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContactUs',
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      },
      submitting: false,
      openFAQ: null
    }
  },
  methods: {
    async submitForm() {
      this.submitting = true;
      
      try {
        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Show success message
        this.$bvToast.toast('Your message has been sent successfully! We\'ll get back to you soon.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        
        // Reset form
        this.form = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        };
        
      } catch (error) {
        this.$bvToast.toast('There was an error sending your message. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.submitting = false;
      }
    },
    
    toggleFAQ(faqNumber) {
      this.openFAQ = this.openFAQ === faqNumber ? null : faqNumber;
    }
  }
}
</script>

<style scoped>
/* Add any custom styles if needed */
</style> 